import {
    Avatar,
    Divider,
    Drawer,
    DrawerProps,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Theme,
    useMediaQuery,
} from '@material-ui/core';
import { ExitToAppOutlined, Home, SettingsOutlined } from '@material-ui/icons';
import ArrowBack from '@material-ui/icons/ArrowBack';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import MenuIcon from '@material-ui/icons/Menu';
import clsx from 'clsx';
import { selectUserAvatar } from 'modules/profile/store/profileSelector';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink } from 'react-router-dom';
import { ClientPicker } from 'shared/components/verticalMenu/ClientPicker';
import { useMenuStyles } from 'shared/components/verticalMenu/styles';
import { VerticalMenuIds } from 'shared/components/verticalMenu/testIds';
import { VerticalMenuCategory } from 'shared/components/verticalMenu/VerticalMenuCategory';
import { IMenuCategory } from 'shared/models/Global';
import { routes } from 'shared/routes';
import { isMediumScreenBreakpoint, isSmallScreenBreakpoint } from 'shared/styles/constants';
import { useLogoutAction } from 'shared/utils/hooks/useLogoutAction';
import { impersonateSwitchBack } from 'store/components/auth/authActions';
import { Permission } from 'store/components/auth/authModels';
import { selectCurrentUser, selectIsUserImpersonate, selectUserPermissions } from 'store/components/auth/selectors';
import { useSidebarState } from 'store/entities/appConfig/utils';
import { selectClientDisplayName, selectCurrentClientId } from 'store/entities/clients/selectors/clientsSelectors';
import { selectConfigurationThemeById } from 'store/entities/clients/selectors/themeSelectors';

export interface IVerticalMenuProps extends Pick<DrawerProps, 'PaperProps' | 'variant'> {
    menu: IMenuCategory[];
}

const VerticalMenu: React.FC<IVerticalMenuProps> = ({ menu, ...other }: IVerticalMenuProps) => {
    const classes = useMenuStyles();
    const dispatch = useDispatch();
    const isSmallScreen = useMediaQuery(
        (theme: Theme) => theme.breakpoints.down(isSmallScreenBreakpoint), { noSsr: true },
    );

    const clientId = useSelector(selectCurrentClientId);
    const configuration = useSelector(selectConfigurationThemeById(clientId ?? ''));
    const permissions = useSelector(selectUserPermissions);
    const tenantName = useSelector(selectClientDisplayName);
    const { isSidebarOpened, setSidebarOpened } = useSidebarState();
    const isMenuTemporary = useMediaQuery((theme: Theme) => theme.breakpoints.down(isMediumScreenBreakpoint));
    const isMinimized = !isMenuTemporary && !isSidebarOpened;
    const currentUser = useSelector(selectCurrentUser);
    const logout = useLogoutAction();
    const isUserImpersonate = useSelector(selectIsUserImpersonate);
    const switchBack = useCallback(() => {
        dispatch(impersonateSwitchBack.init());
    }, [dispatch]);

    const closeMenu = useCallback(event => {
        event.preventDefault();
        setSidebarOpened(false);
    }, [setSidebarOpened]);

    const onClick = useCallback(() => {
        if (isSmallScreen) {
            setSidebarOpened(false);
        }
    }, [isSmallScreen, setSidebarOpened]);

    const handleLogout = () => {
        dispatch(logout());
    };
    const avatar = useSelector(selectUserAvatar);

    return (
        <Drawer onClose={closeMenu} classes={{
            paper: clsx(classes.paper, { [classes.sidebarMinimized]: isMinimized }),
        }}
        open={!isMenuTemporary || isSidebarOpened}
        data-testid={VerticalMenuIds.Container}
        variant="permanent" {...other}
        >
            <List disablePadding component="nav"
                onClick={onClick} className={classes.list}
            >
                <ListItem component={Link} to="/"
                    className={clsx(
                        classes.item, classes.itemCategory, classes.logoItem,
                        { [classes.minimizedLogoWrapper]: !isSidebarOpened },
                    )}
                >
                    <div className={classes.logoWrapper}>
                        {isMinimized ? (
                            <img
                                alt="Noor"
                                className={classes.logoImageMinimized}
                                src={`${process.env.PUBLIC_URL}/images/nss_logo_square.png`}
                            />
                        ) : (
                            <img
                                alt="Noor"
                                className={classes.logoImage}
                                src={`${process.env.PUBLIC_URL}/images/nss-logo.png`}
                            />
                        )}
                        {isSidebarOpened && (
                            <IconButton
                                color="inherit"
                                aria-label="close drawer"
                                onClick={closeMenu}
                                className={classes.hideMenuButton}
                                data-testid={VerticalMenuIds.HideMenu}
                            >
                                <ChevronLeft classes={{ root: classes.chevronMargin }} />
                                <MenuIcon />
                            </IconButton>
                        )}
                    </div>
                </ListItem>
                {!process.env.REACT_APP_CLIENT && tenantName ? (
                    <ClientPicker
                        configuration={configuration}
                        tenantName={tenantName}
                        clientId={clientId}
                        isMinimized={isMinimized}
                        isSmallScreen={isSmallScreen}
                    />
                ) : (
                    <ListItem component={NavLink} to="/"
                        exact
                        className={clsx(classes.item, classes.itemCategory)}
                    >
                        <ListItemIcon className={clsx(classes.itemIcon)}>
                            <Home />
                        </ListItemIcon>
                        <ListItemText>
                            Home
                        </ListItemText>
                    </ListItem>
                )}

                {menu.map((category, index) => (
                    <VerticalMenuCategory
                        key={category.id}
                        category={category}
                        index={index}
                        permissions={permissions}
                        isMinimized={isMinimized}
                        onSetSidebarOpen={setSidebarOpened}
                    />
                ))}
            </List>
            <List
                disablePadding
                className={classes.bottomMenu}
                onClick={closeMenu}
            >
                <Divider />
                {permissions.includes(Permission.modifyClientSettings) && (
                    <ListItem
                        component={NavLink}
                        to={routes.SETTINGS}
                        className={classes.item}
                    >
                        <ListItemIcon className={classes.itemIcon}>
                            <SettingsOutlined />
                        </ListItemIcon>
                        <ListItemText
                            classes={{
                                primary: classes.itemPrimary,
                            }}
                        >
                            Administration
                        </ListItemText>
                    </ListItem>
                )}
                {isSmallScreen && (
                    <>
                        {isUserImpersonate ? (
                            <ListItem
                                className={classes.item}
                                onClick={switchBack}
                            >
                                <ListItemIcon className={classes.itemIcon}>
                                    <ArrowBack />
                                </ListItemIcon>
                                <ListItemText
                                    classes={{
                                        primary: classes.itemPrimary,
                                    }}
                                >
                                    Switch Back
                                </ListItemText>
                            </ListItem>
                        ) : (
                            <ListItem
                                className={classes.item}
                                component={NavLink}
                                to={routes.USER_PROFILE}
                            >
                                <ListItemIcon className={classes.itemIcon}>
                                    <Avatar className={classes.avatar} src = {avatar.url}>
                                        {currentUser?.firstName.substring(0, 1)}{currentUser?.lastName.substring(0, 1)}
                                    </Avatar>
                                </ListItemIcon>
                                <ListItemText
                                    classes={{
                                        primary: classes.itemPrimary,
                                    }}
                                >
                                    User Profile
                                </ListItemText>
                            </ListItem>
                        )}
                        <ListItem className={classes.item} onClick={handleLogout}>
                            <ListItemIcon className={classes.itemIcon}>
                                <ExitToAppOutlined />
                            </ListItemIcon>
                            <ListItemText
                                classes={{
                                    primary: classes.itemPrimary,
                                }}
                            >
                            Logout
                            </ListItemText>
                        </ListItem>
                    </>
                )}
            </List>
        </Drawer>
    );
};

export default VerticalMenu;
